import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { productService } from "../../../Api/Services/productService";
import { productBrandService } from "../../../Api/Services/productBrandService";
import { productCategoryService } from "../../../Api/Services/productCategoryService";
import { productModelService } from "../../../Api/Services/productModelService";
import { ProductImportResult } from "../../../Models/ProductImport";
import { ProductBrand } from "../../../Models/ProductBrand";
import { ProductCategory } from "../../../Models/ProductCategory";
import { ProductModel } from "../../../Models/ProductModel";
import { CommercialUnit } from "../../../Enums/CommercialUnit";
import { WeightUnit } from "../../../Enums/WeightUnit";
import { LengthUnit } from "../../../Enums/LengthUnit";
import { getProductOriginOptions, getProductCommercialUnitLabel, getWeightUnitLabel, getLengthUnitLabel } from "../../../Utils/Enums";
import { FaArrowLeft, FaCheckCircle, FaDownload, FaExclamationTriangle, FaFileCsv, FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import Button from "../../../Components/Button/Button";

const AdminProductImportPage = () => {
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ProductImportResult | null>(null);

  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [models, setModels] = useState<ProductModel[]>([]);
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        setIsLoadingLookups(true);
        const [brandsData, categoriesData, modelsData] = await Promise.all([
          productBrandService.getAll(),
          productCategoryService.getAll(),
          productModelService.getAll()
        ]);
        setBrands(brandsData);
        setCategories(categoriesData);
        setModels(modelsData);
      } catch {
        toast.error("Falha ao carregar marcas, categorias ou modelos.");
      } finally {
        setIsLoadingLookups(false);
      }
    };

    loadLookups();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Selecione um arquivo CSV para importar.");
      return;
    }

    setIsUploading(true);
    setResult(null);
    try {
      const data = await productService.importProducts(selectedFile);
      setResult(data);
      toast.success(`Importação concluída. ${data.created} produto(s) criado(s).`);
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Não foi possível importar os produtos.";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const header = [
      "Nome",
      "Código",
      "Marca",
      "Preço",
      "Descrição",
      "Quantidade em estoque",
      "Ativo",
      "Disponível para compra",
      "Disponível sob encomenda",
      "Categoria",
      "Modelo",
      "Origem",
      "Unidade Comercial",
      "Texto referente às unidades na caixa",
      "NCM",
      "Código de Barras",
      "Peso",
      "Altura",
      "Largura",
      "Comprimento",
      "Especificação 1",
      "Valor da Especificação 1",
      "Especificação 2",
      "Valor da Especificação 2",
      "Especificação 3",
      "Valor da Especificação 3"
    ].join(";");

    const sampleRows = [
      [
        "Ferramenta",
        "FER-001",
        "Marca",
        "129,90",
        "Ferramenta para uso profissional",
        "25",
        "Sim",
        "Sim",
        "Não",
        "Ferramentas",
        "Linha X",
        "Nacional",
        "UN",
        "",
        "82055900",
        "7890000000000",
        "0,55kg",
        "10cm",
        "5cm",
        "30cm",
        "Peso",
        "550 g",
        "Cor",
        "Preto"
      ].join(";")
    ];

    const blob = new Blob([`${header}\n${sampleRows.join("\n")}`], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo-importacao-produtos.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadResult = () => {
    if (!result) {
      return;
    }

    const header = [
      "Linha",
      "Nome",
      "Código",
      "Marca",
      "Categoria",
      "Modelo",
      "Preço",
      "Estoque",
      "Peso",
      "Altura",
      "Largura",
      "Comprimento",
      "Status",
      "Erro"
    ].join(";");

    const lines = result.rows.map((row) =>
      [
        row.rowNumber,
        row.name ?? "",
        row.code ?? "",
        row.brand ?? "",
        row.category ?? "",
        row.model ?? "",
        row.price ?? "",
        row.stockQuantity ?? "",
        formatMeasurementValue(row.weightValue, row.weightUnit),
        formatMeasurementValue(row.heightValue, row.heightUnit),
        formatMeasurementValue(row.widthValue, row.widthUnit),
        formatMeasurementValue(row.lengthValue, row.lengthUnit),
        row.success ? "Sucesso" : "Falhou",
        row.error ?? ""
      ].join(";")
    );

    const blob = new Blob([`${header}\n${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "resultado-importacao-produtos.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatMeasurementValue = (value?: number | null, unit?: WeightUnit | LengthUnit | null) => {
    if (value == null) {
      return "";
    }

    if (unit == null) {
      return value.toString().replace(".", ",");
    }

    if (unit === WeightUnit.G || unit === WeightUnit.KG) {
      return `${value.toString().replace(".", ",")}${getWeightUnitLabel(unit)}`;
    }

    return `${value.toString().replace(".", ",")}${getLengthUnitLabel(unit as LengthUnit)}`;
  };

  const summary = useMemo(() => {
    if (!result) {
      return null;
    }
    const succeeded = result.rows.filter((r) => r.success).length;
    const failed = result.rows.length - succeeded;
    return { succeeded, failed };
  }, [result]);

  const requiredFields = [
    "Nome",
    "Código (único)",
    "Marca",
    "Preço",
    "Quantidade em estoque",
    "Ativo",
    "Disponível para compra",
    "Categoria",
    "Origem",
    "Unidade comercial",
    "NCM"
  ];

  const originOptions = getProductOriginOptions();
  const unitOptions = [
    CommercialUnit.UN,
    CommercialUnit.KG,
    CommercialUnit.CX,
    CommercialUnit.MT,
    CommercialUnit.LT,
    CommercialUnit.M2,
    CommercialUnit.M3
  ];
  const weightUnitOptions = [WeightUnit.KG, WeightUnit.G];
  const lengthUnitOptions = [LengthUnit.M, LengthUnit.CM, LengthUnit.MM];
  return (
    <div>
      <SmartNavbar />
      <div className="p-4 sm:p-14 max-w-full space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-primary-600 text-2xl font-bold">
            <FaFileCsv />
            <span>{"IMPORTAR PRODUTOS"}</span>
          </div>
          <Button
            onClick={() => navigate("/admin/produtos")}
            className="w-full sm:w-auto flex justify-center items-center gap-2 px-4 py-2"
          >
            <FaArrowLeft /> {"Voltar para lista de produtos"}
          </Button>
        </div>

        <p className="text-sm text-gray-700">
          {"Importe um arquivo CSV para cadastrar produtos em massa. Validamos código único por produto, enumerações já cadastradas (marca, categoria, modelo, origem e unidade) e NCM/código de barras."}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="p-4 bg-primary-50 border border-primary-100 rounded">
              <div className="flex items-start gap-3 text-sm text-gray-700">
                <FaInfoCircle className="text-primary-600 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <p className="font-semibold">{"Formato esperado:"}</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Separador ";" (padrão) ou ",". Cabeçalho deve conter os campos do modelo.</li>
                    <li>Campos obrigatórios: {requiredFields.join(", ")}.</li>
                    <li>Código deve ser único (checamos duplicidades no arquivo e já existentes).</li>
                    <li>Marca/Categoria/Modelo devem existir exatamente como cadastrados (sem criar novos).</li>
                    <li>Origem: {originOptions.map(o => o.label).join(" / ")}.</li>
                    <li>Unidade: {unitOptions.map(u => getProductCommercialUnitLabel(u)).join(" / ")}.</li>
                    <li>
                      Peso/Dimensões (opcionais): unidades aceitas peso ({weightUnitOptions.map(u => getWeightUnitLabel(u)).join(" / ")})
                      e dimensões ({lengthUnitOptions.map(u => getLengthUnitLabel(u)).join(" / ")}). Exemplos: 0,55kg, 550 g, 10cm, 0,1m, 100mm. Sem unidade, usamos kg e cm.
                    </li>
                    <li>NCM: 2 ou 8 dígitos. Código de barras: 8, 12, 13 ou 14 dígitos (opcional).</li>
                    <li>Unidade CX exige preencher "Texto referente às unidades na caixa".</li>
                    <li>Não importamos componentes nem imagens; apenas especificações (par chave/valor).</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border rounded">
              <div className="bg-primary-120 border-b px-4 py-2 text-sm font-semibold">{"Exemplo de preenchimento"}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary-120">
                      <th className="border px-3 py-2 text-left">Nome</th>
                      <th className="border px-3 py-2 text-left min-w-[90px] whitespace-nowrap">Código</th>
                      <th className="border px-3 py-2 text-left">Marca</th>
                      <th className="border px-3 py-2 text-left min-w-[90px] whitespace-nowrap">Preço</th>
                      <th className="border px-3 py-2 text-left min-w-[180px]">Descrição</th>
                      <th className="border px-3 py-2 text-left min-w-[80px]">Estoque</th>
                      <th className="border px-3 py-2 text-left">Ativo</th>
                      <th className="border px-3 py-2 text-left min-w-[110px] whitespace-nowrap">Disponível</th>
                      <th className="border px-3 py-2 text-left min-w-[130px] whitespace-nowrap">Sob encomenda</th>
                      <th className="border px-3 py-2 text-left">Categoria</th>
                      <th className="border px-3 py-2 text-left">Modelo</th>
                      <th className="border px-3 py-2 text-left">Origem</th>
                      <th className="border px-3 py-2 text-left min-w-[80px]">Unidade</th>
                      <th className="border px-3 py-2 text-left min-w-[160px] whitespace-nowrap">Texto unidades na caixa</th>
                      <th className="border px-3 py-2 text-left min-w-[110px]">NCM</th>
                      <th className="border px-3 py-2 text-left min-w-[140px] whitespace-nowrap">Código de Barras</th>
                      <th className="border px-3 py-2 text-left min-w-[90px]">Peso</th>
                      <th className="border px-3 py-2 text-left min-w-[90px]">Altura</th>
                      <th className="border px-3 py-2 text-left min-w-[90px]">Largura</th>
                      <th className="border px-3 py-2 text-left min-w-[110px]">Comprimento</th>
                      <th className="border px-3 py-2 text-left min-w-[140px]">Especificação 1</th>
                      <th className="border px-3 py-2 text-left min-w-[120px] whitespace-nowrap">Valor 1</th>
                      <th className="border px-3 py-2 text-left min-w-[140px]">Especificação 2</th>
                      <th className="border px-3 py-2 text-left min-w-[120px] whitespace-nowrap">Valor 2</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="border px-3 py-2">Ferramenta</td>
                      <td className="border px-3 py-2 whitespace-nowrap">FER-001</td>
                      <td className="border px-3 py-2">Marca</td>
                      <td className="border px-3 py-2 whitespace-nowrap">129,90</td>
                      <td className="border px-3 py-2">Ferramenta para uso profissional</td>
                      <td className="border px-3 py-2 whitespace-nowrap">25</td>
                      <td className="border px-3 py-2">Sim</td>
                      <td className="border px-3 py-2">Sim</td>
                      <td className="border px-3 py-2">Não</td>
                      <td className="border px-3 py-2">Ferramentas</td>
                      <td className="border px-3 py-2">Linha X</td>
                      <td className="border px-3 py-2">Nacional</td>
                      <td className="border px-3 py-2">UN</td>
                      <td className="border px-3 py-2"></td>
                      <td className="border px-3 py-2 whitespace-nowrap">82055900</td>
                      <td className="border px-3 py-2 whitespace-nowrap">7890000000000</td>
                      <td className="border px-3 py-2 whitespace-nowrap">0,55kg</td>
                      <td className="border px-3 py-2 whitespace-nowrap">10cm</td>
                      <td className="border px-3 py-2 whitespace-nowrap">5cm</td>
                      <td className="border px-3 py-2 whitespace-nowrap">30cm</td>
                      <td className="border px-3 py-2">Peso</td>
                      <td className="border px-3 py-2 whitespace-nowrap">550g</td>
                      <td className="border px-3 py-2">Cor</td>
                      <td className="border px-3 py-2">Preto</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="border rounded p-4 space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">{"Arquivo CSV"}</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    disabled={isUploading}
                  />
                  <p className="text-xs text-gray-500 mt-1">{"Tamanho máximo recomendado: 2MB."}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <Button isLoading={isUploading}
                    type="submit"
                    className="py-2 flex items-center justify-center gap-2"
                    disabled={isUploading}
                  >
                    {"IMPORTAR PRODUTOS"}
                  </Button>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="flex items-center justify-center gap-2 text-primary-700 hover:text-primary-800 text-sm"
                  >
                    <FaDownload /> {"Baixar modelo CSV"}
                  </button>
                </div>

                {isUploading && (
                  <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded px-3 py-2">
                    <FaInfoCircle />
                    <span>{"Processando arquivo e criando produtos..."}</span>
                  </div>
                )}
              </div>
            </form>

            {result && (
              <div className="border rounded p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FaCheckCircle className="text-green-600" />
                  <span>{"Resultado da importação"}</span>
                </div>
                <p className="text-sm text-gray-700">
                  {"Total de linhas lidas"}: {result.totalRows || result.rows.length}
                </p>
                <p className="text-sm text-gray-700">{"Criados"}: {result.created}</p>
                <p className="text-sm text-gray-700">{"Falhas"}: {result.failed}</p>
                {summary && summary.failed > 0 && (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-2">
                    <FaExclamationTriangle className="mt-0.5" />
                    <span>{"Alguns registros falharam. Veja detalhes abaixo ou baixe o CSV de resultado."}</span>
                  </div>
                )}
                <button
                  onClick={handleDownloadResult}
                  className="flex items-center gap-2 text-primary-700 hover:text-primary-800 text-sm"
                >
                  <FaDownload /> {"Baixar resultado"}
                </button>
              </div>
            )}

            <div className="border rounded p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FaInfoCircle className="text-primary-600" />
                <span>{"Valores cadastrados"}</span>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">Marcas ({brands.length})</p>
                <div className="flex flex-wrap gap-2">
                  {brands.slice(0, 30).map(b => (
                    <span key={b.id} className="px-2 py-1 bg-gray-100 rounded text-xs">{b.name}</span>
                  ))}
                  {brands.length > 30 && <span className="text-xs text-gray-500">+{brands.length - 30} mais...</span>}
                </div>
                <p className="font-semibold mt-2">Categorias ({categories.length})</p>
                <div className="flex flex-wrap gap-2">
                  {categories.slice(0, 30).map(c => (
                    <span key={c.id} className="px-2 py-1 bg-gray-100 rounded text-xs">{c.name}</span>
                  ))}
                  {categories.length > 30 && <span className="text-xs text-gray-500">+{categories.length - 30} mais...</span>}
                </div>
                <p className="font-semibold mt-2">Modelos ({models.length})</p>
                <div className="flex flex-wrap gap-2">
                  {models.slice(0, 30).map(m => (
                    <span key={m.id} className="px-2 py-1 bg-gray-100 rounded text-xs">{m.name}</span>
                  ))}
                  {models.length > 30 && <span className="text-xs text-gray-500">+{models.length - 30} mais...</span>}
                </div>
                {isLoadingLookups && <p className="text-xs text-gray-500">{"Carregando valores..."}</p>}
              </div>
            </div>
          </div>
        </div>

        {result && (
          <div className="border rounded">
            <div className="bg-primary-120 border-b px-4 py-2 text-sm font-semibold flex justify-between items-center">
              <span>{"Detalhes"}</span>
              <span className="text-xs text-gray-500">
                {result.rows.length} {"linhas processadas"}
              </span>
            </div>
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-primary-120">
                  <tr>
                    <th className="border px-3 py-2 text-left">Linha</th>
                    <th className="border px-3 py-2 text-left">Nome</th>
                    <th className="border px-3 py-2 text-left">Código</th>
                    <th className="border px-3 py-2 text-left">Marca</th>
                    <th className="border px-3 py-2 text-left">Categoria</th>
                    <th className="border px-3 py-2 text-left">Modelo</th>
                    <th className="border px-3 py-2 text-left">Preço</th>
                    <th className="border px-3 py-2 text-left">Estoque</th>
                    <th className="border px-3 py-2 text-left">Peso</th>
                    <th className="border px-3 py-2 text-left">Altura</th>
                    <th className="border px-3 py-2 text-left">Largura</th>
                    <th className="border px-3 py-2 text-left">Comprimento</th>
                    <th className="border px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.rowNumber} className="bg-white">
                      <td className="border px-3 py-2">{row.rowNumber}</td>
                      <td className="border px-3 py-2">{row.name ?? "-"}</td>
                      <td className="border px-3 py-2">{row.code ?? "-"}</td>
                      <td className="border px-3 py-2">{row.brand ?? "-"}</td>
                      <td className="border px-3 py-2">{row.category ?? "-"}</td>
                      <td className="border px-3 py-2">{row.model ?? "-"}</td>
                      <td className="border px-3 py-2">{row.price ?? "-"}</td>
                      <td className="border px-3 py-2">{row.stockQuantity ?? "-"}</td>
                      <td className="border px-3 py-2">{formatMeasurementValue(row.weightValue, row.weightUnit) || "-"}</td>
                      <td className="border px-3 py-2">{formatMeasurementValue(row.heightValue, row.heightUnit) || "-"}</td>
                      <td className="border px-3 py-2">{formatMeasurementValue(row.widthValue, row.widthUnit) || "-"}</td>
                      <td className="border px-3 py-2">{formatMeasurementValue(row.lengthValue, row.lengthUnit) || "-"}</td>
                      <td className="border px-3 py-2">
                        {row.success ? (
                          <span className="text-green-700">{"Criado"}</span>
                        ) : (
                          <div className="text-red-700 flex flex-col">
                            <span>{"Falhou"}</span>
                            {row.error && <span className="text-xs text-red-600">{row.error}</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProductImportPage;
